import "./style.scss";

import {
    type CSSProperties,
    forwardRef,
    type ReactNode,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    type UIEvent,
} from "react";

export interface ScrollShadowWrapperProps {
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
}

// https://www.qovery.com/blog/adding-elegant-shadows-with-react-to-invite-users-to-scroll
// https://github.com/Qovery/console/blob/staging/libs/shared/ui/src/lib/components/scroll-shadow-wrapper/scroll-shadow-wrapper.tsx

const ScrollShadowWrapper = forwardRef<HTMLDivElement, ScrollShadowWrapperProps>((props, ref) => {
    const { children, className = "", style = {} } = props;

    const [scrollTop, setScrollTop] = useState(0);
    const [scrollHeight, setScrollHeight] = useState(0);
    const [clientHeight, setClientHeight] = useState(0);

    const onScrollHandler = (event: UIEvent<HTMLDivElement>) => {
        setScrollTop(event.currentTarget.scrollTop);
        setScrollHeight(event.currentTarget.scrollHeight);
        setClientHeight(event.currentTarget.clientHeight);
    };

    const wrapperRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => wrapperRef.current as HTMLDivElement);

    useEffect(() => {
        if (!wrapperRef.current) return;
        setScrollTop(wrapperRef.current.scrollTop);
        setScrollHeight(wrapperRef.current.scrollHeight);
        setClientHeight(wrapperRef.current.clientHeight);
    });

    const getVisibleSides = (): { top: boolean; bottom: boolean } => {
        const isBottom = clientHeight === scrollHeight - Math.floor(scrollTop);
        const isTop = scrollTop === 0;
        const isBetween = scrollTop > 0 && clientHeight < scrollHeight - scrollTop;

        return {
            top: (isBottom || isBetween) && !(isTop && isBottom),
            bottom: (isTop || isBetween) && !(isTop && isBottom),
        };
    };

    return (
        <div ref={wrapperRef} style={style} className={`scroll-shadow-wrapper ${className}`} onScroll={onScrollHandler}>
            <div className="scroll-shadow-top" style={{ opacity: getVisibleSides().top ? 1 : 0 }} />
            {children}
            <div className="scroll-shadow-bottom" style={{ opacity: getVisibleSides().bottom ? 1 : 0 }} />
        </div>
    );
});

ScrollShadowWrapper.displayName = "ScrollShadowWrapper";

export default ScrollShadowWrapper;
